import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { environment } from '@environment';
import {
  FindSupportChatsOpts,
  FollowUpEmailMode,
  ROLES_KEYS,
  SupportChatInList,
  SupportChatMarker,
  SupportChatsOrder,
  SupportingCompanyAccessLevel,
  User,
} from '@interfaces';
import { SupportChatFacade } from '@state/support-chat';
import { debounce } from 'lodash-es';
import { map, ReplaySubject, take, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { FollowUpEmailModalComponent } from '@management-components/follow-up-email-modal/follow-up-email-modal.component';
import { FollowUpChatModalComponent } from '@management-components/follow-up-chat-drawer/follow-up-chat-modal.component';
import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';
import { ConfirmationModalService } from '@shared/confirmation-modal/confirmation-modal.service';
import { supportChatFilters } from '@shared/animations/support-chat-filters';
import { SupportChatWindowDesktopDrawerService } from '@shared/support-chat-window/support-chat-window-desktop-drawer/support-chat-window-desktop-drawer.service';
import {
  ContextMenuItemConfig,
  SUPPORT_CHAT_CONTEXT_MENU_ITEMS,
} from '@shared/support-chat/support-chat-context-menu.config';
import { UserAuthFacade } from '@state/user-auth';

@Component({
  selector: 'app-support-chat-desktop',
  templateUrl: './support-chat-desktop.component.html',
  styleUrls: ['./support-chat-desktop.component.scss'],
  animations: [supportChatFilters],
  standalone: false,
})
export class SupportChatDesktopComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly userAuthFacade = inject(UserAuthFacade);
  private readonly supportChatFacade = inject(SupportChatFacade);
  private readonly stackedMessagesService = inject(StackedMessagesService);
  private readonly confirmationModal = inject(ConfirmationModalService);
  private readonly supportChatWindowDesktopDrawerService = inject(SupportChatWindowDesktopDrawerService);
  public dialog = inject(MatDialog);

  private destroy$: ReplaySubject<boolean> = new ReplaySubject(1);

  @Output() searchTypeChange = new EventEmitter<ROLES_KEYS.Foreigner | ROLES_KEYS.Employer>();

  @ViewChild('chatsList') chatsList: ElementRef<HTMLElement>;

  public foreignersSupportChatsList$ = this.supportChatFacade.foreignersSupportChatsList$.pipe(
    map(hugeObject => {
      return Object.values(hugeObject);
    })
  );

  public employersSupportChatsList$ = this.supportChatFacade.employersSupportChatsList$.pipe(
    map(hugeObject => {
      return Object.values(hugeObject);
    })
  );
  public myself$ = this.userAuthFacade.myself$;
  public openSupportChat$ = this.supportChatFacade.openSupportChat$;
  public loadingChatsList$ = this.supportChatFacade.loadingChatsList$;
  public loadingMoreChats$ = this.supportChatFacade.loadingMoreChats$;
  public loadingTranscriptionChat$ = this.supportChatFacade.loadingTranscriptionChat$;

  public myUnreadCountForeigners$ = this.supportChatFacade.myUnreadCountForeigners$;
  public myUnreadCountEmployers$ = this.supportChatFacade.myUnreadCountEmployers$;

  public pagination$ = this.supportChatFacade.pagination$;

  public myself: User;

  public debouncedSearch = debounce(opts => this.searchSupportChats(opts), 400);

  public SupportChatsOrder = SupportChatsOrder;
  public SupportChatMarker = SupportChatMarker;
  public SupportingCompanyAccessLevel = SupportingCompanyAccessLevel;
  public showMarkerButtonFor: string = null;
  public API_URL = environment.API_URL;

  public allChatsLoaded = false;

  public isChatFiltersVisible = true;

  public activeMenuId: string;
  public menuHeightMaxForeigner = 392;
  public menuHeightMaxEmployer = 248;
  public menuHeight = 0;
  public menuDirection: 'up' | 'down' = 'down';
  public menuTop = 0;
  public isMenuVisible = false;

  private readonly offsetDown = 40;
  private readonly offsetUp = 75;

  private debouncedOnscroll = debounce(({ target, srcElement }) => {
    const { clientHeight } = srcElement as HTMLElement;
    const { scrollTop, scrollHeight } = target as HTMLElement;
    const shouldLoadMore = scrollHeight - scrollTop < clientHeight + 100;

    if (shouldLoadMore) {
      this.loadMoreSupportChats();
    }
  }, 150);

  public availableMenuItems: ContextMenuItemConfig[] = [];
  public actionHandlers: Record<string, (chat: SupportChatInList) => void> = {
    markAttention: chat => this.addSupportChatMarker(SupportChatMarker.ATTENTION_REQUIRED, chat.id),
    markLater: chat => this.addSupportChatMarker(SupportChatMarker.COMEBACK_LATER, chat.id),
    removeMarker: chat => this.removeSupportChatMarker(chat.id),
    markAsUnread: chat => this.markAsUnread(chat.id),
    followUpChat: chat => this.openFollowupDialogChat(chat),
    followUpEmail: chat => this.openFollowupDialogEmail(chat),
    getTranscription: chat => this.getTranscription(chat),
    none: () => {},
  };

  protected readonly ROLES_KEYS = ROLES_KEYS;

  public ngOnInit(): void {
    this.myself$.pipe(take(1), takeUntil(this.destroy$)).subscribe(myself => {
      this.myself = myself;
      this.loadMenuItems();
    });

    this.pagination$.pipe(takeUntil(this.destroy$)).subscribe(pagi => {
      this.allChatsLoaded = pagi.count === 0 || pagi.count >= pagi.all;
    });

    this.pagination$.pipe(take(1), takeUntil(this.destroy$)).subscribe(pagination => {
      this.supportChatFacade.searchSupportChats({
        ...pagination,
        onlyAssignedToMe: true,
        onlyUsersWithRole: ROLES_KEYS.Foreigner,
        orderBy: SupportChatsOrder.LAST_MESSAGE_DATE,
      });
    });

    this.loadingChatsList$.pipe(takeUntil(this.destroy$)).subscribe(isLoading => {
      if (!isLoading && this.chatsList?.nativeElement) {
        this.chatsList.nativeElement.scrollTop = 0;
      }
    });
  }

  public ngAfterViewInit(): void {
    this.chatsList?.nativeElement.addEventListener('scroll', this.debouncedOnscroll);
  }

  public ngOnDestroy(): void {
    this.supportChatWindowDesktopDrawerService.close();
    this.destroy$.next(true);
    this.destroy$.complete();
    this.chatsList?.nativeElement.removeAllListeners('scroll');
  }

  public searchSupportChats(providedOpts: Partial<FindSupportChatsOpts>): void {
    this.pagination$.pipe(take(1), takeUntil(this.destroy$)).subscribe(pagination => {
      const opts: FindSupportChatsOpts = {
        ...pagination,
        ...providedOpts,
        offset: 0,
        limit: 25,
      };

      this.supportChatFacade.searchSupportChats(opts);
    });
  }

  public loadMoreSupportChats(): void {
    this.pagination$.pipe(take(1), takeUntil(this.destroy$)).subscribe(pagination => {
      // we've loaded all of the notifications
      if (pagination.count >= pagination.all) {
        this.allChatsLoaded = true;
        return;
      }

      const opts: FindSupportChatsOpts = {
        ...pagination,
        offset: pagination.offset + 25,
        limit: 25,
      };
      this.supportChatFacade.loadMoreSupportChats(opts);
    });
  }

  public addSupportChatMarker(marker: SupportChatMarker, supportChatId: string): void {
    this.supportChatFacade.addSupportChatMarker({ marker, supportChatId });
  }

  public removeSupportChatMarker(supportChatId: string): void {
    this.supportChatFacade.removeSupportChatMarker(supportChatId);
  }

  public markAsUnread(supportChatId: string): void {
    this.supportChatFacade.markSupportChatAsUnread(supportChatId);
  }

  public openFollowupDialogChat(supportChat: SupportChatInList): void {
    this.dialog.open(FollowUpChatModalComponent, {
      height: 'min(50vh, calc(var(--app-visual-viewport-height, 100vh) - 56px))',
      width: '50vw',
      data: { supportChat },
    });
  }

  public openFollowupDialogEmail(supportChat: SupportChatInList): void {
    this.dialog.open(FollowUpEmailModalComponent, {
      height: 'min(80vh, calc(var(--app-visual-viewport-height, 100vh) - 56px))',
      width: '50vw',
      data: { users: [supportChat.supportedUser], mode: FollowUpEmailMode.SUPPORT_CHAT },
    });
  }

  public getTranscription(supportChat: SupportChatInList): void {
    this.confirmationModal
      .open({
        confirmationToTranslate: 'MANAGEMENT.CHAT.GET_TRANSCRIPTION_MODAL',
        translateParams: { userName: supportChat.supportedUser.name },
      })
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(res => {
        if (!res) {
          return;
        }

        this.supportChatFacade.getTranscriptionSuccess$
          .pipe(take(1), takeUntil(this.destroy$), takeUntil(this.supportChatFacade.getTranscriptionError$))
          .subscribe(() => {
            this.stackedMessagesService.showInfo({ message: 'MANAGEMENT.CHAT.GET_TRANSCRIPTION_SUCCESS' });
          });

        this.supportChatFacade.getTranscription(supportChat.id);
      });
  }

  public openSupportChat(supportChatToOpen: SupportChatInList): void {
    this.openSupportChat$.pipe(take(1), takeUntil(this.destroy$)).subscribe(currentlyOpennedSupportChat => {
      // If no currentlu open support chat - open new and nothing else
      if (!currentlyOpennedSupportChat) {
        this.supportChatFacade.openSupportChat(supportChatToOpen.id);
        return;
      }

      // If user want open the same chat, cancel
      if (currentlyOpennedSupportChat.supportChat.id === supportChatToOpen.id) {
        this.supportChatFacade.setMinimizedStatus(false);
        return;
      }

      // if currenlty some chat openned - close it succesfully and only then open new one
      this.supportChatFacade.closeSupportChatSuccess$.pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
        setTimeout(() => {
          this.supportChatFacade.openSupportChat(supportChatToOpen.id);
        });
      });

      this.supportChatFacade.setMinimizedStatus(false);
      this.supportChatFacade.closeSupportChat(currentlyOpennedSupportChat.supportChat.id);
    });
  }

  public toggleMenu(event: MouseEvent, chatId: string, type: ROLES_KEYS.Foreigner | ROLES_KEYS.Employer): void {
    event.stopPropagation();

    const trigger = event.currentTarget as HTMLElement;
    const rect = trigger.getBoundingClientRect();

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    let menuHeightMax = 0;

    if (type === ROLES_KEYS.Foreigner) {
      menuHeightMax = this.menuHeightMaxForeigner;
    }

    if (type === ROLES_KEYS.Employer) {
      menuHeightMax = this.menuHeightMaxEmployer;
    }

    const fitsBelow = spaceBelow >= menuHeightMax;
    const fitsAbove = spaceAbove >= menuHeightMax;
    const preferBelow = spaceBelow > spaceAbove;

    if (fitsBelow) {
      this.menuDirection = 'down';
      this.menuTop = rect.bottom - this.offsetDown;
      this.menuHeight = menuHeightMax;
      this.setMenuState(chatId);
      return;
    }

    if (fitsAbove) {
      this.menuDirection = 'up';
      this.menuTop = rect.top - menuHeightMax - this.offsetUp;
      this.menuHeight = menuHeightMax;
      this.setMenuState(chatId);
      return;
    }

    if (preferBelow) {
      this.menuDirection = 'down';
      this.menuTop = rect.bottom - this.offsetDown;
      this.menuHeight = Math.max(spaceBelow - 20, 200);
      this.setMenuState(chatId);
      return;
    }

    this.menuDirection = 'up';
    this.menuTop = Math.max(20, rect.top - spaceAbove + 20) - this.offsetUp;
    this.menuHeight = Math.max(spaceAbove - 20, 200);
    this.setMenuState(chatId);
  }

  private setMenuState(chatId: string): void {
    this.activeMenuId = this.activeMenuId === chatId ? null : chatId;
    this.isMenuVisible = this.activeMenuId === chatId;
  }

  public closeMenus(event?: MouseEvent): void {
    this.activeMenuId = null;

    if (event) {
      event.stopPropagation();
    }
  }

  @HostListener('document:click')
  public onOutsideClick(): void {
    this.closeMenus();
  }

  private loadMenuItems(): void {
    this.availableMenuItems = SUPPORT_CHAT_CONTEXT_MENU_ITEMS.filter(
      item => !item.permissionKey || this.hasPermission(item.permissionKey)
    );
  }

  public hasPermission(key: string): boolean {
    return this.myself.role.permissions?.some(permission => permission.key === key);
  }

  public tabChanged(selectedTabIndex?: number): void {
    if (selectedTabIndex === 0) {
      this.pagination$.pipe(take(1), takeUntil(this.destroy$)).subscribe(pagination => {
        this.searchTypeChange.emit(ROLES_KEYS.Foreigner);

        this.supportChatFacade.searchSupportChats({
          ...pagination,
          offset: 0,
          onlyAssignedToMe: true,
          onlyUsersWithRole: ROLES_KEYS.Foreigner,
          orderBy: SupportChatsOrder.LAST_MESSAGE_DATE,
        });
      });
    }

    if (selectedTabIndex === 1) {
      this.pagination$.pipe(take(1), takeUntil(this.destroy$)).subscribe(pagination => {
        this.searchTypeChange.emit(ROLES_KEYS.Employer);

        this.supportChatFacade.searchSupportChats({
          ...pagination,
          offset: 0,
          onlyAssignedToMe: false,
          onlyUsersWithRole: ROLES_KEYS.Employer,
          orderBy: SupportChatsOrder.LAST_MESSAGE_DATE,
        });
      });
    }
  }
}
